import { Injectable, Injector } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { GoogleAnalyticsService } from '@timechef/core/analytics';
import { ApiOrdersService, ApiTicketsService, DateOnlyDto } from '@timechef/core/api';
import { AuthRepository } from '@timechef/core/auth';
import { AppErrorKeys, ensureAppError } from '@timechef/core/errors/error';
import { I18nService } from '@timechef/core/i18n/i18n.service';
import { ILogger, LoggerService } from '@timechef/core/logging';
import { FileService } from '@timechef/core/services/file-service';
import { AlertRadioChoice, InteractionService } from '@timechef/core/services/interaction.service';
import { dateOnlyDtoToApi, ensureDate, formatWithLang } from '@timechef/core/utils';
import { isNullOrUndefined } from '@timechef/core/utils/js-utils';
import { capitalizeFirstLetter } from '@timechef/core/utils/string-utils';
import { AccountRepository } from '@timechef/features/account/state';
import { RestaurantsRepository } from '@timechef/features/restaurants/state';
import { format, startOfMonth, subMonths } from 'date-fns';
import { catchError, tap, throwError } from 'rxjs';
import { OrdersModalComponent, OrdersModalComponentResult, OrdersModalOption } from '../history-home/containers/orders-modal/orders-modal.component';
import { TicketsModalComponent } from '../history-home/containers/tickets-modal/tickets-modal.component';
import { HistoryRepository, HistoryStatusKeys, buildCancelOrderKey, buildTableNumberKey } from './history.repository';
import { isTableValid, mapOrderDtoToOrder } from './history.utils';

const HISTORY_PAGE_SIZE = 10;

@Injectable({
  providedIn: 'root',
})
export class HistoryService {
  logger: ILogger;
  private authRepo: AuthRepository;
  constructor(
    private accountRepo: AccountRepository,
    private alertCtrl: AlertController,
    injector: Injector,
    private i18n: I18nService,
    private interactionService: InteractionService,
    private historyRepo: HistoryRepository,
    private restaurantRepo: RestaurantsRepository,
    private apiTickets: ApiTicketsService,
    private apiOrders: ApiOrdersService,
    private modalCtrl: ModalController,
    private googleAnalyticsService: GoogleAnalyticsService,
    private fileService: FileService,
    loggerService: LoggerService,
  ) {
    this.logger = loggerService.getLogger('HistoryService');
    this.authRepo = injector.get(AuthRepository);
  }

  getOrders(params: { pageIndex?: number; restaurantIdWithExternalOrderHistory?: number }) {
    let req: Parameters<typeof this.apiOrders.getOrders>[0] = {
      SiteName: this.authRepo.getCurrentSiteName(),
    };

    if (!isNullOrUndefined(params.pageIndex)) {
      req = { ...req, PageIndex: params.pageIndex, PageSize: HISTORY_PAGE_SIZE };
    } else if (params.restaurantIdWithExternalOrderHistory) {
      req = { ...req, RestaurantWithExternalOrderHistoryId: params.restaurantIdWithExternalOrderHistory };
    }

    return this.apiOrders.getOrders(req).pipe(
      this.historyRepo.ordersByPeriodDS.trackRequestStatus(),
      tap((data) => {
        if (data.list)
          this.historyRepo.addOrders(
            data.list!.orders.map((x) => mapOrderDtoToOrder(x)),
            data.list!.hasMore,
            req!.PageIndex!,
          );
        else if (data.external) this.historyRepo.setExternalOrders(data.external);
      }),
    );
  }

  getStandaloneOrder(orderId: number) {
    return this.apiOrders.getByOrderid({ OrderId: orderId }).pipe(
      this.historyRepo.standaloneOrderDS.trackRequestStatus(),
      tap((data) => {
        this.historyRepo.setStandaloneOrder(mapOrderDtoToOrder(data.order));
      }),
    );
  }

  getTickets(pageIndex: number) {
    return this.apiTickets.getTickets({ SiteName: this.authRepo.getCurrentSiteName(), PageIndex: pageIndex, PageSize: HISTORY_PAGE_SIZE }).pipe(
      this.historyRepo.ticketsByPeriodDS.trackRequestStatus(),
      tap((data) => {
        this.historyRepo.addTickets(data.tickets, data.hasMore, pageIndex);
      }),
    );
  }

  getTicketPdfByIndex(index: number) {
    const ticket = this.historyRepo.getTicketByIndex(index);
    (ticket as any).date = new Date(ticket.date);
    return this.apiTickets.postGeneratePdf({ body: { ticket } }).pipe(
      this.historyRepo.trackRequestStatus(HistoryStatusKeys.GeneratePdf),
      tap((blobStr: any) => {
        this.historyRepo.setPdfAsGenerated();
        this.fileService
          .getBlobAndOpenIt(
            blobStr,
            this.i18n.translate(`TICKETS_SERVICE.TICKET_PDF_FILENAME`, { date: format(ensureDate(ticket.date), 'yyyyMMdd-HHmmss') }),
            'application/pdf',
          )
          .then()
          .catch(async (e: any) => {
            await this.errorHandling(e);
          });
      }),
      catchError(async (e) => {
        await this.errorHandling(e);
        return throwError(() => e);
      }),
    );
  }

  async promptAskMonthTicketsBatchPdf() {
    const startOfCurrentMonth = startOfMonth(new Date());
    const monthes = [startOfCurrentMonth, subMonths(startOfCurrentMonth, 1)];

    const header = this.i18n.translate(`TICKETS_SERVICE.TICKETS_PDF_BATCH_ALERT.HEADER`);
    const msg = this.i18n.translate(`TICKETS_SERVICE.TICKETS_PDF_BATCH_ALERT.MESSAGE`);

    const choices: AlertRadioChoice<DateOnlyDto>[] = monthes.map((x, i) => ({
      label: capitalizeFirstLetter(formatWithLang(x, 'MMMM yyyy', this.accountRepo.getLanguage()!)),
      value: { day: x.getDate(), month: x.getMonth(), year: x.getFullYear() },
      checked: i === 0,
    }));

    const month = await this.interactionService.showAlertRadios(header, msg, choices);

    if (isNullOrUndefined(month)) {
      return;
    }

    this.getTicketsBatchPdf(month).subscribe();
  }

  getTicketsBatchPdf(firstDayOfMonth: DateOnlyDto) {
    const firstDayOfMonthApi = dateOnlyDtoToApi(firstDayOfMonth);
    return this.apiTickets
      .postGeneratePdfBatch({
        body: {
          siteName: this.authRepo.getCurrentSiteName(),
          firstDayOfMonth: firstDayOfMonthApi,
        },
      })
      .pipe(
        this.historyRepo.trackRequestStatus(HistoryStatusKeys.GeneratePdfBatch),
        tap((blobStr: any) => {
          this.historyRepo.setPdfBatchAsGenerated();
          this.fileService
            .getBlobAndOpenIt(
              blobStr,
              this.i18n.translate(`TICKETS_SERVICE.TICKETS_PDF_BATCH_FILENAME`, {
                date: `${firstDayOfMonthApi.year}-${firstDayOfMonthApi.month.toString().padStart(2, '0')}`,
              }),
              'application/pdf',
            )
            .then()
            .catch(async (e: any) => {
              await this.errorHandling(e);
            });
        }),
        catchError(async (e) => {
          await this.errorHandling(e);
          return throwError(() => e);
        }),
      );
  }

  async errorHandling(e: any) {
    // If error is blob, try to read it as text and convert it to error payload
    if (e.payload instanceof Blob) {
      try {
        const asText = await e.payload.text();
        e = JSON.parse(asText);
      } catch (e) {
        this.logger.error(e);
        // do nothing
      }
    }

    const alert = await this.alertCtrl.create({
      message: this.i18n.translateError(ensureAppError(e).errorKey),
      buttons: ['OK'],
    });

    await alert.present();
  }

  async openTicketModal(ticketId: number) {
    const modal = await this.modalCtrl.create({
      component: TicketsModalComponent,
      componentProps: { ticketId },
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/ticket', 'Ticket | Details');
  }

  async openOrderModal(orderId: number, options?: OrdersModalOption): Promise<OrdersModalComponentResult | undefined> {
    const modal = await this.modalCtrl.create({
      component: OrdersModalComponent,
      componentProps: { orderId, options },
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/order', 'Order | Details');

    const { data } = await modal.onDidDismiss<OrdersModalComponentResult>();
    return data;
  }

  cancelOrder(orderId: number) {
    return this.apiOrders.postByOrderidCancel$Response({ OrderId: orderId }).pipe(
      this.historyRepo.trackRequestStatus(buildCancelOrderKey(orderId)),

      tap(async () => {
        // silent refresh page 0
        this.getOrders({ pageIndex: 0 }).subscribe();

        const alert = await this.alertCtrl.create({
          header: this.i18n.translate('RESTAURANT_SERVICE.CANCEL_ORDER_SUCCESS_ALERT.HEADER'),
          message: this.i18n.translate(`RESTAURANT_SERVICE.CANCEL_ORDER_SUCCESS_ALERT.MESSAGE`),
          buttons: ['OK'],
          cssClass: 'confirm-alert',
        });

        await alert.present();
        this.restaurantRepo.removeOrderFromScorp(orderId);
        this.historyRepo.setCancelOrderSuccess(orderId);
        await alert.onDidDismiss();
      }),
    );
  }

  tryAssignTableNumberToAnOrder(tableNumber: string) {
    // open a modal or smth loader with status

    // Table is not valid
    if (isNaN(+tableNumber)) {
      this.interactionService.showToaster({
        message: this.i18n.translateError(AppErrorKeys.ScanInvalidData),
        color: 'danger',
        duration: 5000,
      });
      return;
    }

    // No scorp order available
    const scorpOrderToday = this.historyRepo.getSingleScorpOrderWithDate(ensureDate(new Date()));
    if (!scorpOrderToday) {
      this.interactionService.showToaster({
        message: this.i18n.translateError(AppErrorKeys.NoScorpOrderToday),
        color: 'danger',
        duration: 5000,
      });
      return;
    }
    const isTableNumberValid = isTableValid(+tableNumber, scorpOrderToday);
    if (!isTableNumberValid) {
      this.interactionService.showToaster({
        message: this.i18n.translateError(AppErrorKeys.NumeroTableInvalid),
        color: 'danger',
        duration: 5000,
      });
      return;
    }

    this.openOrderModal(scorpOrderToday.id).then();

    return this.editOrderTableNumber(scorpOrderToday.id, +tableNumber);
  }

  editOrderTableNumber(orderId: number, tableNumber?: number) {
    return this.apiOrders.patchByOrderidTablenumber$Response({ OrderId: orderId, body: { tableNumber } }).pipe(
      this.historyRepo.trackRequestStatus(buildTableNumberKey(orderId)),

      tap(async () => {
        // silent refresh page 0
        this.getOrders({ pageIndex: 0 }).subscribe();

        const alert = await this.alertCtrl.create({
          header: this.i18n.translate('RESTAURANT_SERVICE.UPDATE_TABLE_NUMBER_SUCCESS_ALERT.HEADER'),
          message: this.i18n.translate(`RESTAURANT_SERVICE.UPDATE_TABLE_NUMBER_SUCCESS_ALERT.MESSAGE`),
          buttons: ['OK'],
          cssClass: 'confirm-alert',
        });

        await alert.present();
        this.historyRepo.updateTableNumber(orderId, tableNumber);
        await alert.onDidDismiss();
      }),
    );
  }
}
